
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { toast } from '@/utils/toast';
import * as notificationService from '@/services/notificationService';

// Define notification types
export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  isRead: boolean;
  createdAt: string;
  link?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: () => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'userId' | 'createdAt' | 'isRead'>) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;
  
  // Fetch notifications from server when user changes
  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setLoading(false);
      setError(null);
    }
  }, [currentUser]);

  // Fetch notifications from the API
  const fetchNotifications = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fetchedNotifications = await notificationService.fetchUserNotifications(currentUser.uid);
      setNotifications(fetchedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError(error instanceof Error ? error : new Error('Failed to fetch notifications'));
      // Don't show toast to user, just log error
    } finally {
      setLoading(false);
    }
  };

  // Mark a notification as read
  const markAsRead = async (id: string) => {
    if (!currentUser) return;
    
    try {
      await notificationService.markNotificationAsRead(id, currentUser.uid);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, isRead: true } : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Silent error handling - don't show toast to user
      throw error; // Re-throw for component handling
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!currentUser || notifications.length === 0) return;
    
    try {
      await notificationService.markAllNotificationsAsRead(currentUser.uid);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      
      // Only show success toast for this action since it's explicitly triggered by user
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Silent error handling
    }
  };

  // Add a new notification
  const addNotification = async (notification: Omit<Notification, 'id' | 'userId' | 'createdAt' | 'isRead'>) => {
    if (!currentUser) return;
    
    try {
      const newNotification = await notificationService.createNotification({
        ...notification,
        userId: currentUser.uid
      });
      
      // Update local state
      setNotifications(prev => [newNotification, ...prev]);
      
      // Show toast for new notification
      toast.info(notification.title, {
        description: notification.message
      });
    } catch (error) {
      console.error('Error creating notification:', error);
      // Silent error handling
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    addNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
